<template>
  <div v-if="showApp">
    <div class="operation-box" id="operation-box">
      <div class="action-bar">
        <div class="action-bar-cont">
          <div>
            <div class="add-file-btn" @click.stop="openInsertMenu">{{ multiTxt.add }}
              <div class="pdf-insert-close" @click.stop="closeInsertMenu"></div>
              <ul class="pdf-insert-menu">
                <li @click="triggerFileInput($event)">{{ multiTxt.addFiles }}</li>
                <li @click.stop="addBlankPage($event)">{{ multiTxt.blankPage }}</li>
              </ul>
            </div>
            <div class="clear-pdf-btn" :title="multiTxt.clear" @click="resetPages(true)">{{ multiTxt.clear }}</div>
            <p class="drag-describe">{{ multiTxt.rearrange }}</p>
          </div>
          <div class="action-bar-right">
            <div class="toggle-switch">
              <div :class="['page-view', { act: !isMergedMode }]" @click="toggleMode(false)"><figure></figure><p class="toggle-text-full">{{ multiTxt.pageview }}</p><p class="toggle-text-short">{{ multiTxt.page }}</p></div>
              <div :class="['file-view', { act: isMergedMode }]" @click="toggleMode(true)"><figure></figure><p class="toggle-text-full">{{ multiTxt.fileview }}</p><p class="toggle-text-short">{{ multiTxt.file }}</p></div>
            </div>
            <button class="export-pdf-btn" @click="getResult(true)" :disabled="!pdfPages.length">{{ multiTxt.save }}</button>
          </div>
        </div>

      </div>
      <input type="file" ref="fileInput" @change="handleFileUpload" accept="application/pdf,image/jpeg,image/png,image/webp,image/svg+xml,image/bmp,.heic,.heif,.jfif,.pjpeg,.pjp" multiple style="display: none" />

      <div class="pdf-views" @dragover.prevent="onDragOver" @dragleave.prevent="onDragLeave" @drop.prevent="onDrop">
        <div class="pdf-pages">
          <div v-for="(page) in displayPages" :key="`pdf-${page.pdfId}-page-${page.pageNumber}`" class="pdf-page-item"
            :class="{ 'multiple-pages': page.pageCount > 1, 'image-type': page.type === 'image', 'image-landscape': page.type === 'image' && page.isLandscape }">
            <div class="pdf-page-info" :id="`pdf-${page.pdfId}-page-${page.pageNumber}`"
              :data-pagecount="page.pageCount || 1" :data-pdfId="page.pdfId || 1"
              :data-pageNumber="page.pageNumber || 1">
              <figure class="page-placeholder" @click="previewPage"><div class="page-loading"></div></figure>
              <div class="page-text">
                <p class="page-file-name" :class="page.type === 'blank' ? 'name-bgcolor-blank' : `name-bgcolor-${page.nameColor % 8 || 0}`" :title="page.pdfName">{{
                  page.pdfName }}</p>
                <p class="page-number" :style="{ opacity: (page.type === 'image' || page.type === 'blank') ? 0 : 1 }">
                  {{
                    isMergedMode
                      ? `${page.pageCount} ${page.pageCount > 1 ? multiTxt.pages : multiTxt.page}`
                      : page.pageNumber
                  }}
                </p>
              </div>
              <div class="page-page-grab"></div>
              <button class="delete-page-btn" @click="removePage(page.pdfId, page.pageNumber, page.pageCount)"></button>
            </div>
            <div class="pdf-page-insert">
              <button class="pdf-page-add" @click.stop="openInsertMenu"></button>
              <div class="pdf-insert-close" @click.stop="closeInsertMenu"></div>
              <ul class="pdf-insert-menu">
                <li @click="addNewFileAfterPage(page)">{{ multiTxt.addFiles }}</li>
                <li @click.stop="addBlankPage($event, page)">{{ multiTxt.blankPage }}</li>
              </ul>
              <div class="pdf-insert-closemo" @click.stop="closeInsertMenu"></div>
            </div>
          </div>
          <div class="pdf-page-plus" draggable="false">
            <p>{{ multiTxt.addto }}</p>
            <ul class="pdf-plus-menu">
              <li class="pdf-plus-file" @click="triggerFileInput($event)">{{ multiTxt.addFiles }}</li>
              <li class="pdf-plus-blank" @click.stop="addBlankPage($event)">{{ multiTxt.blankPage }}</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
    <div class="operation-error" v-if="showError">
      <p v-html="errorNames"></p>
      <div class="operation-error-close" @click="showError = false">×</div>
    </div>
  </div>
</template>

<script setup>
/* eslint-disable no-undef */
import {
  ref,
  nextTick, onMounted, onUnmounted,
} from "vue";
import * as pdfjsLib from "pdfjs-dist";
pdfjsLib.GlobalWorkerOptions.workerSrc = `https://www.pdfgear.com/js/pdf-2.10.377.worker.min.js`;
import Sortable from "sortablejs";

let PDFLib = null;
let downloadFileUrl = "";
const showApp = ref(false);
const showError = ref(false)
const errorNames = ref("")
const firstNum = 15;
const bgColor = ref(0);
const fileAll = ref({});
const fileMap = ref({});
const pdfPages = ref([]);
const displayPages = ref([]);
const fileInput = ref(null);
const isMergedMode = ref(false);
const sortable = ref(null);
const a4Size = { width: 595, height: 842 };

const multiLang = {
  en: {
    rearrange: `Drag and drop to rearrange the order.`,
    add: `Add`,
    addFiles: `Add Files`,
    blankPage: `Blank Page`,
    clear: `Clear`,
    pageview: `Page View`,
    fileview: `File View`,
    file: `File`,
    save: `Save`,
    addto: `Add to your document`,
    sureclear: `Are you sure to clear all pages added?`,
    page: `Page`,
    pages: `Pages`,
    fail: `Fail to add `,
    files: ` file(s):`,
  },
  de: {
    rearrange: `Ziehen und ablegen, um die Reihenfolge zu ändern.`,
    add: `Hinzufügen`,
    addFiles: `Dateien hinzufügen`,
    blankPage: `Leere Seite`,
    clear: `Alles löschen`,
    pageview: `Seitenansicht`,
    fileview: `Dateiansicht`,
    file: `Datei`,
    save: `Speichern`,
    addto: `Zu Ihrem Dokument hinzufügen`,
    sureclear: `Möchten Sie wirklich alle hinzugefügten Seiten löschen?`,
    page: `Seite`,
    pages: `Seiten`,
    fail: ``,
    files: ` Datei(en) konnten nicht hinzugefügt werden:`,
  },
  fr: {
    rearrange: `Glissez-déposez pour réorganiser l’ordre.`,
    add: `Ajouter`,
    addFiles: `Ajouter des fichiers`,
    blankPage: `Page vierge`,
    clear: `Tout effacer`,
    pageview: `Vue par page`,
    fileview: `Vue par fichier`,
    file: `Fichier`,
    save: `Enregistrer`,
    addto: `Ajouter à votre document`,
    sureclear: `Voulez-vous vraiment supprimer toutes les pages ajoutées ?`,
    page: `Page`,
    pages: `Pages`,
    fail: `Échec de l’ajout de `,
    files: ` fichier(s) :`,
  },
  it: {
    rearrange: `Trascina e rilascia per riordinare.`,
    add: `Aggiungi`,
    addFiles: `Aggiungi file`,
    blankPage: `Pagina vuota`,
    clear: `Cancella tutto`,
    pageview: `Vista pagine`,
    fileview: `Vista file`,
    file: `File`,
    save: `Salva`,
    addto: `Aggiungi al tuo documento`,
    sureclear: `Vuoi davvero eliminare tutte le pagine aggiunte?`,
    page: `Pagina`,
    pages: `Pagine`,
    fail: `Impossibile aggiungere `,
    files: ` file:`,
  },
  pt: {
    rearrange: `Arraste e solte para reorganizar a ordem.`,
    add: `Adicionar`,
    addFiles: `Adicionar arquivos`,
    blankPage: `Página em branco`,
    clear: `Limpar tudo`,
    pageview: `Visualização de página`,
    fileview: `Visualização de arquivo`,
    file: `Arquivo`,
    save: `Salvar`,
    addto: `Adicionar ao seu documento`,
    sureclear: `Tem certeza de que deseja excluir todas as páginas adicionadas?`,
    page: `Página`,
    pages: `Páginas`,
    fail: `Falha ao adicionar `,
    files: ` arquivo(s):`,
  },
  es: {
    rearrange: `Arrastra y suelta para reorganizar el orden.`,
    add: `Añadir`,
    addFiles: `Añadir archivos`,
    blankPage: `Página en blanco`,
    clear: `Borrar todo`,
    pageview: `Vista de página`,
    fileview: `Vista de archivo`,
    file: `Archivo`,
    save: `Guardar`,
    addto: `Añadir a su documento`,
    sureclear: `¿Seguro que quieres eliminar todas las páginas añadidas?`,
    page: `Página`,
    pages: `Páginas`,
    fail: `No se pudo añadir `,
    files: ` archivo(s):`,
  },
  id: {
    rearrange: `Seret dan lepas untuk mengatur ulang urutan.`,
    add: `Tambah`,
    addFiles: `Tambahkan File`,
    blankPage: `Halaman Kosong`,
    clear: `Hapus Semua`,
    pageview: `Tampilan Halaman`,
    fileview: `Tampilan File`,
    file: `File`,
    save: `Simpan`,
    addto: `Tambahkan ke dokumen Anda`,
    sureclear: `Apakah Anda yakin ingin menghapus semua halaman yang telah ditambahkan?`,
    page: `Halaman`,
    pages: `Halaman`,
    fail: `Gagal menambahkan `,
    files: ` file:`,
  },
  vi: {
    rearrange: `Kéo và thả để sắp xếp lại thứ tự.`,
    add: `Thêm`,
    addFiles: `Thêm tệp`,
    blankPage: `Trang trống`,
    clear: `Xóa Tất Cả`,
    pageview: `Chế độ xem Trang`,
    fileview: `Chế độ xem Tệp`,
    file: `Tệp`,
    save: `Lưu`,
    addto: `Thêm vào tài liệu của bạn`,
    sureclear: `Bạn có chắc chắn muốn xóa tất cả các trang đã thêm không?`,
    page: `Trang`,
    pages: `Các trang`,
    fail: `Không thể thêm `,
    files: ` tệp:`,
  },
  ru: {
    rearrange: `Перетащите, чтобы изменить порядок.`,
    add: `Добавить`,
    addFiles: `Добавить файлы`,
    blankPage: `Пустая страница`,
    clear: `Очистить всё`,
    pageview: `Просмотр страниц`,
    fileview: `Просмотр файлов`,
    file: `Файл`,
    save: `Сохранить`,
    addto: `Добавить в документ`,
    sureclear: `Вы уверены, что хотите удалить все добавленные страницы?`,
    page: `Страница`,
    pages: `Страницы`,
    fail: `Не удалось добавить `,
    files: ` файл(ов):`,
  },
  ja: {
    rearrange: `ドラッグ＆ドロップで順序を並べ替えます。`,
    add: `追加`,
    addFiles: `ファイルを追加`,
    blankPage: `空白ページ`,
    clear: `すべてクリア`,
    pageview: `ページ表示`,
    fileview: `ファイル表示`,
    file: `ファイル`,
    save: `保存`,
    addto: `ドキュメントに追加`,
    sureclear: `追加したすべてのページを削除してもよろしいですか？`,
    page: `ページ`,
    pages: `ページ`,
    fail: ``,
    files: ` 件のファイルの追加に失敗しました:`,
  },
  ar: {
    rearrange: `اسحب وأفلت لإعادة ترتيب الترتيب.`,
    add: `إضافة`,
    addFiles: `إضافة ملفات`,
    blankPage: `صفحة فارغة`,
    clear: `مسح الكل`,
    pageview: `عرض الصفحات`,
    fileview: `عرض الملفات`,
    file: `الملف`,
    save: `حفظ`,
    addto: `إضافة إلى مستندك`,
    sureclear: `هل أنت متأكد من أنك تريد حذف جميع الصفحات المضافة؟`,
    page: `صفحة`,
    pages: `صفحات`,
    fail: `فشل في إضافة `,
    files: ` ملف(ات):`,
  }
}
const multiTxt = multiLang[document.documentElement.lang.slice(0, 2)] || multiLang["en"]

const imageExtReg = /\.(jpe?g|jfif|pjpeg|pjp|png|webp|svg|bmp|heic|heif)$/i;
const jpegExtReg = /\.(jpe?g|jfif|pjpeg|pjp)$/i;
const imageMimeTypes = new Set([
  "image/jpeg",
  "image/pjpeg",
  "image/png",
  "image/x-png",
  "image/webp",
  "image/svg+xml",
  "image/bmp",
  "image/x-ms-bmp",
  "image/heic",
  "image/heif",
  "image/heic-sequence",
  "image/heif-sequence",
]);

const createFileId = (prefix) => prefix + Math.random().toString(36).substring(2, 9);
const isPdfFile = (file) => file.type === "application/pdf" || /\.pdf$/i.test(file.name);
const isImageFile = (file) => imageMimeTypes.has((file.type || "").toLowerCase()) || imageExtReg.test(file.name);
const isHeicFile = (file) => /image\/hei[cf]/i.test(file.type || "") || /\.(heic|heif)$/i.test(file.name);
const isJpegFile = (file) => /image\/p?jpe?g/i.test(file.type || "") || jpegExtReg.test(file.name);
const isLandscapeSize = (size) => Number(size?.width) > Number(size?.height);
const getA4Size = (isLandscape = false) => (
  isLandscape
    ? { width: a4Size.height, height: a4Size.width }
    : { ...a4Size }
);

const updateImageLandscape = (fileId, isLandscape) => {
  [...pdfPages.value, ...displayPages.value].forEach((page) => {
    if (page.pdfId === fileId && page.type === "image") {
      page.isLandscape = isLandscape;
    }
  });
};

const loadImage = (url) => new Promise((resolve, reject) => {
  const img = new Image();
  img.onload = () => resolve(img);
  img.onerror = () => reject(new Error("Invalid image file"));
  img.src = url;
});

const canvasToBlob = (canvas, type, quality) => new Promise((resolve, reject) => {
  canvas.toBlob((blob) => {
    if (blob) {
      resolve(blob);
    } else {
      reject(new Error("Image conversion failed"));
    }
  }, type, quality);
});

const hasAlphaChannel = (context, width, height) => {
  const data = context.getImageData(0, 0, width, height).data;
  for (let i = 3; i < data.length; i += 4) {
    if (data[i] < 255) return true;
  }
  return false;
};

const normalizeImageFile = async (file) => {
  let sourceBlob = file;
  if (isHeicFile(file)) {
    const { default: heic2any } = await import("heic2any");
    const converted = await heic2any({ blob: file, toType: "image/png", quality: 0.92 });
    sourceBlob = Array.isArray(converted) ? converted[0] : converted;
  }

  const sourceUrl = URL.createObjectURL(sourceBlob);
  try {
    const img = await loadImage(sourceUrl);
    const width = img.naturalWidth || img.width;
    const height = img.naturalHeight || img.height;
    if (!width || !height) throw new Error("Invalid image file");

    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext("2d", { willReadFrequently: true });
    if (!context) throw new Error("Image conversion failed");

    context.drawImage(img, 0, 0, width, height);
    const embedType = !isJpegFile(file) && hasAlphaChannel(context, width, height) ? "png" : "jpg";
    const blob = await canvasToBlob(
      canvas,
      embedType === "png" ? "image/png" : "image/jpeg",
      embedType === "png" ? undefined : 0.92
    );

    return { blob, width, height, embedType };
  } finally {
    URL.revokeObjectURL(sourceUrl);
  }
};

const setImageFileData = async (fileId, imageData) => {
  const fileInfo = fileMap.value[fileId];
  if (!fileInfo) return null;

  const oldUrl = fileInfo.url;
  const imageFileData = await imageData.blob.arrayBuffer();
  if (fileMap.value[fileId] !== fileInfo) return null;

  Object.assign(fileInfo, {
    url: URL.createObjectURL(imageData.blob),
    width: imageData.width,
    height: imageData.height,
    embedType: imageData.embedType,
    failed: false,
    isLandscape: isLandscapeSize(imageData),
  });
  updateImageLandscape(fileId, fileInfo.isLandscape);
  fileAll.value[fileId] = imageFileData;
  if (oldUrl) URL.revokeObjectURL(oldUrl);
  return fileInfo;
};

const setImageFailed = (fileId) => {
  const fileInfo = fileMap.value[fileId];
  if (!fileInfo) return null;
  if (fileInfo.url) URL.revokeObjectURL(fileInfo.url);

  Object.assign(fileInfo, {
    url: "",
    embedType: "",
    failed: true,
    isLandscape: false,
  });
  updateImageLandscape(fileId, false);
  delete fileAll.value[fileId];
  return fileInfo;
};

const ensureImageReady = async (fileId) => {
  const fileInfo = fileMap.value[fileId];
  if (fileInfo?.type !== "image") return null;
  if (fileInfo.failed || (fileInfo.url && fileAll.value[fileId])) return fileInfo;

  if (!fileInfo.readyPromise) {
    fileInfo.readyPromise = normalizeImageFile(fileInfo.file)
      .then((imageData) => setImageFileData(fileId, imageData))
      .catch((error) => {
        console.error("Error processing image:", error);
        return setImageFailed(fileId);
      })
      .finally(() => {
        if (fileMap.value[fileId]) {
          fileMap.value[fileId].readyPromise = null;
        }
      });
  }

  return fileInfo.readyPromise;
};

const cleanupFile = (fileId) => {
  const fileInfo = fileMap.value[fileId];
  if (fileInfo?.type === "image" && fileInfo.url) {
    URL.revokeObjectURL(fileInfo.url);
  }
  delete fileMap.value[fileId];
  delete fileAll.value[fileId];
};

const cleanupUnusedFile = (fileId) => {
  if (!pdfPages.value.some((page) => page.pdfId === fileId)) {
    cleanupFile(fileId);
  }
};

const cleanupAllFiles = () => {
  Object.keys(fileMap.value).forEach(cleanupFile);
};

const createPagesByFile = (file, colorIndex) => {
  const fileId = file.fileId;
  const fileInfo = fileMap.value[fileId];
  const pageCount = file.type === "image" ? 1 : fileInfo.numPages;

  return Array.from({ length: pageCount }, (_, index) => ({
    pdfId: fileId,
    type: file.type || "pdf",
    nameColor: colorIndex,
    pdfName: file.file.name,
    pageNumber: index + 1,
    isLandscape: file.type === "image" ? !!fileInfo.isLandscape : false,
  }));
};

const createBlankPage = (size) => {
  const fileId = createFileId("blank");
  fileMap.value[fileId] = {
    type: "blank",
    width: size.width || a4Size.width,
    height: size.height || a4Size.height,
  };

  return {
    pdfId: fileId,
    type: "blank",
    nameColor: "blank",
    pdfName: "Blank Page",
    pageNumber: 1,
  };
};

const createBlankCanvas = (size, maxSize = 360) => {
  const width = size?.width || a4Size.width;
  const height = size?.height || a4Size.height;
  const scale = Math.min(1, maxSize / Math.max(width, height));
  const canvas = document.createElement("canvas");
  canvas.classList.add("page-canvas");
  canvas.width = Math.max(1, Math.round(width * scale));
  canvas.height = Math.max(1, Math.round(height * scale));
  const context = canvas.getContext("2d");
  if (context) {
    context.fillStyle = "#fff";
    context.fillRect(0, 0, canvas.width, canvas.height);
  }
  return canvas;
};

const getPageSize = async (page) => {
  if (!page) return { ...a4Size };

  const fileInfo = fileMap.value[page.pdfId];
  if (page.type === "blank") {
    return getA4Size(isLandscapeSize(fileInfo));
  }

  if (page.type === "image") {
    const imageInfo = await ensureImageReady(page.pdfId);
    return getA4Size(isLandscapeSize(imageInfo));
  }

  const pdfPage = await fileInfo.getPage(page.pageNumber);
  const viewport = pdfPage.getViewport({ scale: 1 });
  pdfPage.cleanup();
  return { width: viewport.width, height: viewport.height };
};

const renderBlankPage = (page) => {
  const fileInfo = fileMap.value[page.pdfId];
  const pageElement = document.getElementById(`pdf-${page.pdfId}-page-${page.pageNumber}`);
  const pagePlaceholder = pageElement?.querySelector(".page-placeholder");
  if (!pagePlaceholder) return;

  pagePlaceholder.innerHTML = "";
  pagePlaceholder.appendChild(createBlankCanvas(fileInfo));
};

const appendImageToPlaceholder = (pagePlaceholder, img) => {
  const imgBase = document.createElement("div");
  imgBase.className = "page-img-base";
  imgBase.appendChild(img);
  pagePlaceholder.innerHTML = "";
  pagePlaceholder.appendChild(imgBase);
};

const renderBrokenImagePage = (pagePlaceholder, page) => {
  const img = new Image();
  img.src = "#";
  img.alt = page.pdfName;
  img.draggable = false;
  appendImageToPlaceholder(pagePlaceholder, img);
};

const renderImagePage = async (page, currentVersion = renderVersion) => {
  const fileInfo = await ensureImageReady(page.pdfId);
  if (currentVersion !== renderVersion) return;

  const pageElement = document.getElementById(`pdf-${page.pdfId}-page-${page.pageNumber}`);
  const pagePlaceholder = pageElement?.querySelector(".page-placeholder");
  if (!pagePlaceholder) return;

  if (fileInfo?.failed || !fileInfo?.url) {
    renderBrokenImagePage(pagePlaceholder, page);
    return;
  }

  const img = new Image();
  img.src = fileInfo.url;
  img.alt = page.pdfName;
  img.draggable = false;
  appendImageToPlaceholder(pagePlaceholder, img);
};

const addBlankImagePage = (doc, fileInfo) => {
  const width = fileInfo?.width || a4Size.width;
  const height = fileInfo?.height || a4Size.height;
  const scale = Math.min(1, 14400 / Math.max(width, height));
  doc.addPage([width * scale, height * scale]);
};

const addImageToPdf = async (doc, page) => {
  const fileInfo = await ensureImageReady(page.pdfId);
  const fileData = fileAll.value[page.pdfId];
  if (fileInfo?.type !== "image") return;
  if (fileInfo.failed || !fileData) {
    addBlankImagePage(doc, getA4Size(isLandscapeSize(fileInfo)));
    return;
  }

  const image = fileInfo.embedType === "png"
    ? await doc.embedPng(fileData)
    : await doc.embedJpg(fileData);
  const pageSize = getA4Size(fileInfo.isLandscape);
  const pdfPage = doc.addPage([pageSize.width, pageSize.height]);
  const marginX = pageSize.width * 0.05;
  const marginY = pageSize.height * 0.05;
  const fitScale = Math.min(
    (pageSize.width - marginX * 2) / fileInfo.width,
    (pageSize.height - marginY * 2) / fileInfo.height
  );
  const width = fileInfo.width * fitScale;
  const height = fileInfo.height * fitScale;

  pdfPage.drawImage(image, {
    x: (pageSize.width - width) / 2,
    y: (pageSize.height - height) / 2,
    width,
    height,
  });
};

const openInsertMenu = (event) => {
  closeInsertMenu();
  const $target = $(event.target);
  let isHead = ''
  if ($target.hasClass("add-file-btn")) {
    $target.addClass("act");
    isHead = '_head'
  } else {
    $target.closest(".pdf-page-insert").addClass("act");
  }
  dataLayer.push({
    event: 'upload_file2' + isHead
  });
};
const closeInsertMenu = () => {
  $(".pdf-page-insert, .add-file-btn").removeClass("act");
};

const toggleMode = (mergeView) => {
  if(mergeView === isMergedMode.value) return;
  dataLayer.push({
    event: mergeView ? 'switch_fileview': 'switch_pageview'
  });
  if (!isMergedMode.value) {
    pdfPages.value = getResult();
  }
  isMergedMode.value = !isMergedMode.value;
  if (!pdfPages.value.length) return;
  invalidateRenderTasks();
  const currentVersion = renderVersion;
  nextTick(async () => {
    if (currentVersion !== renderVersion) return;
    displayPages.value = getDisplayPages(pdfPages.value);
    await renderAllPages(displayPages.value, currentVersion);
    if (currentVersion !== renderVersion) return;
    initializeSortable();
  });
};

const resetPages = (push = false) => {
  if (push) {
    dataLayer.push({
      event: 'click_clear',
    });
    const ok = confirm(multiTxt.sureclear);
    if (!ok) {
      return;
    }
    dataLayer.push({
      event: 'click_clear_check',
    });
  } 
  cleanupAllFiles();
  bgColor.value = 0;
  pdfPages.value = [];
  displayPages.value = [];
  fileAll.value = {};
  fileMap.value = {};
  if (fileInput.value) {
    fileInput.value.value = null;
  }
  if (sortable.value) {
    sortable.value.destroy();
    sortable.value = null;
  }
  invalidateRenderTasks();
}

const getDisplayPages = (pages) => {
  let result = [];
  if (isMergedMode.value) {
    for (const page of pages) {
      if (!result.length || result[result.length - 1].pdfId !== page.pdfId) {
        result.push({
          ...page,
          pageCount: 1,
        });
      } else {
        result[result.length - 1].pageCount++;
      }
    }
  } else {
    result = pages.map((page) => ({
      ...page,
      pageCount: 1,
    }));
  }
  // console.log("***result***", result);
  return result;
};

onMounted(async () => {

  const file1Element = document.getElementById("file1");
  file1Element.addEventListener("change", handleFileUpload);

  document.querySelectorAll(".pdfcvert-complete-reset, .pdfcvert-complete-readymanage, .startover").forEach(element => {
    element.addEventListener("click", function () {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    });
  });

  document.querySelectorAll(".pdfcvert-delete-area, .startover").forEach(element => {
  element.addEventListener("click", () => {
    $('.continue-tools').hide();
    if (downloadFileUrl) {
      URL.revokeObjectURL(downloadFileUrl);
      downloadFileUrl = "";
    }
  });
});

});

const beforeUnloadHandler = (e) => {
  e.preventDefault();
  dataLayer.push({
    event: 'try_to_leave',
  });
  e.returnValue = '';
}

const handleFileUpload = async (event) => {
  const files = Array.from(event.target.files);

  if (event.target.id == "file1") {
    dataLayer.push({
      event: 'file_selected1'
    });
    document.querySelector(".pdfcvert-before").style.display = "none";
    document.querySelector(".pdfcvert-upload").style.display = "block";
  }else if(event.target.id == 'click-last'){
    dataLayer.push({
      event: 'file_selected2',
      insert_type: 'file'
    });
  } else if(event.target.id == 'click-head'){
    dataLayer.push({
      event: 'file_selected2_head',
      insert_type: 'file'
    });
  }
  
  const validFiles = await checkFiles(files)

  if (validFiles.length) {
    addFiles(validFiles);
  }

};
const escapeHtml = (value) =>
  String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#39;')
  
const checkFiles = async (files) => {
  showError.value = false
  errorNames.value = ""
  let filteredFiles = []
  let unprocessable = []
  let errorArray = []
  for (let i = 0; i < files.length; i++) {
    const file = files[i];
    try {
      if (isPdfFile(file)) {
        const fileData = await file.arrayBuffer();
        const fileDoc = await pdfjsLib.getDocument({
          data: fileData, cMapUrl: "https://www.pdfgear.com/js/cmaps/", cMapPacked: true
        }).promise;
        const fileId = createFileId("pdf");
        fileMap.value[fileId] = fileDoc;
        fileAll.value[fileId] = fileData
        filteredFiles.push({ fileId: fileId, file: file, type: "pdf" });
      } else if (isImageFile(file)) {
        const fileId = createFileId("img");
        fileMap.value[fileId] = {
          type: "image",
          file,
          url: "",
          width: 0,
          height: 0,
          embedType: "",
          failed: false,
          isLandscape: false,
          readyPromise: null,
        };
        filteredFiles.push({ fileId: fileId, file: file, type: "image" });
      } else {
        throw new Error("Unsupported file type");
      }
    } catch (error) {
      unprocessable.push(escapeHtml(file.name.slice(0, 5)));
      console.error('Error processing file:', error);
      errorArray.push(error.message)
      console.log(errorArray)
    }
  }
  if (unprocessable.length) {
    const len = unprocessable.length;
    if (len == files.length && !showApp.value) {
      OnloadCvert();
      
      if(errorArray[0] == 'No password given') {
        window.errorPopup.alert("encrypted");
      } else {
        window.errorPopup.alert("unsupported");
      }
      return []
    }
    const endingSign = len > 5 ? '...' : '.'
    errorNames.value = `<b>${multiTxt.fail}${len}${multiTxt.files}</b> ${unprocessable.slice(0, 5).map(file => `"${file}..."`).join(', ')}${endingSign}`;
    showError.value = true
  }
  return filteredFiles
}


const addFiles = async (files) => {
  if (!showApp.value) {
    showApp.value = true
    document.querySelectorAll(".pdfbox, .infheader").forEach(element => {
      element.style.display = "none";
    });
    dataLayer.push({
      event: 'show_pdflayoutbox',
    });
    window.addEventListener('beforeunload', beforeUnloadHandler);
  }
  pdfPages.value = getResult();
  for (const file of files) {

    bgColor.value += 1;
    const colorIndex = bgColor.value;
    const fileId = file.fileId
    const pages = createPagesByFile(file, colorIndex);
    displayPages.value.push(...getDisplayPages(pages));
    pdfPages.value.push(...pages);
    // console.log(pdfPages, displayPages);

    const currentVersion = renderVersion;
    nextTick(async () => {
      if (currentVersion !== renderVersion) return;
      await renderPages(fileId, currentVersion);
      if (currentVersion !== renderVersion) return;
      initializeSortable();
      if (fileInput.value) {
        fileInput.value.value = null;
      }
    });
  }
}

onUnmounted(() => {
  cleanupAllFiles();
  invalidateRenderTasks();
});

let taskQueue = [];
let isProcessing = false;
let renderVersion = 0;
function invalidateRenderTasks() {
  renderVersion += 1;
  taskQueue = [];
  imageObserver.disconnect();
}
async function enqueueTask(task) {
  taskQueue.push(task);
  await processQueue();
}

async function processQueue() {
  if (isProcessing) return;
  isProcessing = true;

  try {
    while (taskQueue.length > 0) {
      const task = taskQueue.shift();
      if (task) {
        try {
          await task();
        } catch (error) {
          console.error("Thumbnail queue task failed:", error);
        }
      }
    }
  } finally {
    isProcessing = false;
  }
}

const imageObserver = new IntersectionObserver((entries, observer) => {

  entries.forEach((entry) => {

    const infoContent = entry.target;
    const pdfId = infoContent.getAttribute('data-pdfid')
    const pageNumber = infoContent.getAttribute('data-pagenumber')

    if (entry.isIntersecting) {
      const currentVersion = renderVersion;
      enqueueTask(async () => {
        await lazyRender(pdfId, pageNumber, currentVersion);
      });
      observer.unobserve(infoContent);
    }
  });
});

const lazyRender = async (pdfId, pageNumber, currentVersion) => {
  if (currentVersion !== renderVersion) return;

  const pageData = pdfPages.value.find(
    (page) => page.pdfId === pdfId && Number(page.pageNumber) === Number(pageNumber)
  );
  if (pageData?.type === "blank") {
    renderBlankPage(pageData);
    return;
  }
  if (pageData?.type === "image") {
    await renderImagePage(pageData, currentVersion);
    return;
  }

  const pdfDoc = fileMap.value[pdfId];
  if (!pdfDoc) return;

  let page;
  try {
    page = await pdfDoc.getPage(Number(pageNumber));
  } catch (error) {
    console.error(
      `Error loading PDF ${pdfId} page ${pageNumber}:`,
      error
    );
    return;
  }

  if (currentVersion !== renderVersion) {
    page.cleanup();
    return;
  }

  let canvas = document.createElement("canvas");
  const context = canvas.getContext("2d", { willReadFrequently: true });
  if (!context) {
    page.cleanup();
    return;
  }

  const viewport = page.getViewport({ scale: 0.4 });
  canvas.width = viewport.width;
  canvas.height = viewport.height;

  try {
    await page.render({
      canvasContext: context,
      viewport: viewport,
    }).promise;
  } catch (error) {
    console.error(
      `Error rendering PDF ${pdfId} page ${pageNumber}:`,
      error
    );
    context.fillStyle = "#fff";
    context.fillRect(0, 0, canvas.width, canvas.height);
  } finally {
    page.cleanup();
  }

  if (currentVersion !== renderVersion) return;

  const pageId = `pdf-${pdfId}-page-${pageNumber}`;
  const pageElement = document.getElementById(pageId);
  const pagePlaceholder = pageElement?.querySelector(".page-placeholder");
  if (pagePlaceholder) {
    pagePlaceholder.innerHTML = "";
    pagePlaceholder.appendChild(canvas);
    canvas = null;
  }
}

const renderPages = async (filePdfId, currentVersion = renderVersion) => {
  if (currentVersion !== renderVersion) return;

  const pdfDoc = fileMap.value[filePdfId];
  if (!pdfDoc) return;

  const pagesToRender = displayPages.value.filter(
    (page) => page.pdfId === filePdfId
  );
  const maxNum = pagesToRender.length > firstNum ? firstNum : pagesToRender.length

  for (let pageIndex = 0; pageIndex < maxNum; pageIndex++) {
    if (currentVersion !== renderVersion) return;

    const page = pagesToRender[pageIndex];
    const pageId = `pdf-${page.pdfId}-page-${page.pageNumber}`;
    const pageElement = document.getElementById(pageId);
    if (pageElement) {
      if (page.type === "blank") {
        renderBlankPage(page);
        await new Promise((resolve) => setTimeout(resolve, 1));
        continue;
      }

      if (page.type === "image") {
        await renderImagePage(page, currentVersion);
        await new Promise((resolve) => setTimeout(resolve, 1));
        continue;
      }

      let pageObj;
      try {
        pageObj = await pdfDoc.getPage(page.pageNumber);
      } catch (error) {
        console.error(
          `Error loading PDF ${filePdfId} page ${page.pageNumber}:`,
          error
        );
        continue;
      }

      if (currentVersion !== renderVersion) {
        pageObj.cleanup();
        return;
      }

      const viewport = pageObj.getViewport({ scale: 0.4 });
      let canvas = document.createElement("canvas");
      canvas.classList.add("page-canvas");
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) {
        pageObj.cleanup();
        continue;
      }
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      try {
        await pageObj.render({
          canvasContext: context,
          viewport: viewport,
        }).promise;
      } catch (error) {
        console.error(
          `Error rendering PDF ${filePdfId} page ${page.pageNumber}:`,
          error
        );
        context.fillStyle = "#fff";
        context.fillRect(0, 0, canvas.width, canvas.height);
      } finally {
        pageObj.cleanup();
      }

      if (currentVersion !== renderVersion) return;

      const currentPageElement = document.getElementById(pageId);
      const pagePlaceholder = currentPageElement?.querySelector(".page-placeholder");
      if (pagePlaceholder) {
        pagePlaceholder.innerHTML = "";
        pagePlaceholder.appendChild(canvas);
        canvas = null;
      }

      await new Promise((resolve) => setTimeout(resolve, 1));
    }
  }

  if (currentVersion !== renderVersion) return;

  const infoConts = document.querySelectorAll('.pdf-page-info');
  infoConts.forEach((event, index) => {
    if (index < firstNum) {
      return;
    }
    imageObserver.observe(event);
  });
};

const renderAllPages = async (pages, currentVersion = renderVersion) => {
  const maxNum = pages.length > firstNum ? firstNum : pages.length

  for (let i = 0; i < maxNum; i++) {
    if (currentVersion !== renderVersion) return;

    const page = pages[i];
    const fileId = page.pdfId;
    const pdfDoc = fileMap.value[fileId];
    if (!pdfDoc) {
      continue;
    }
    if (page.type === "blank") {
      renderBlankPage(page);
      await new Promise((resolve) => setTimeout(resolve, 1));
      continue;
    }

    if (page.type === "image") {
      await renderImagePage(page, currentVersion);
      await new Promise((resolve) => setTimeout(resolve, 1));
      continue;
    }

    const pageId = `pdf-${fileId}-page-${page.pageNumber}`;
    const pageElement = document.getElementById(pageId);
    if (pageElement) {
      let pageObj;
      try {
        pageObj = await pdfDoc.getPage(page.pageNumber);
      } catch (error) {
        console.error(
          `Error loading PDF ${fileId} page ${page.pageNumber}:`,
          error
        );
        continue;
      }

      if (currentVersion !== renderVersion) {
        pageObj.cleanup();
        return;
      }

      const viewport = pageObj.getViewport({ scale: 0.4 });
      let canvas = document.createElement("canvas");
      canvas.classList.add("page-canvas");
      const context = canvas.getContext("2d", { willReadFrequently: true });
      if (!context) {
        pageObj.cleanup();
        continue;
      }
      canvas.width = viewport.width;
      canvas.height = viewport.height;

      try {
        await pageObj.render({
          canvasContext: context,
          viewport: viewport,
        }).promise;
      } catch (error) {
        console.error(
          `Error rendering PDF ${fileId} page ${page.pageNumber}:`,
          error
        );
        context.fillStyle = "#fff";
        context.fillRect(0, 0, canvas.width, canvas.height);
      } finally {
        pageObj.cleanup();
      }

      if (currentVersion !== renderVersion) return;

      const currentPageElement = document.getElementById(pageId);
      const pagePlaceholder = currentPageElement?.querySelector(".page-placeholder");
      if (pagePlaceholder) {
        pagePlaceholder.innerHTML = "";
        pagePlaceholder.appendChild(canvas);
        canvas = null;
      }

      await new Promise((resolve) => setTimeout(resolve, 1));
    }
  }

  if (currentVersion !== renderVersion) return;

  const infoConts = document.querySelectorAll('.pdf-page-info');
  infoConts.forEach((event, index) => {
    if (index < firstNum) {
      return;
    }
    imageObserver.observe(event);
  });
};


const removePage = (pdfId, pageNumber, pageCount) => {
  if (isMergedMode.value) {

    const targetGroupIndex = pdfPages.value.findIndex(
      (page) => page.pdfId === pdfId && page.pageNumber === pageNumber
    );

    if (targetGroupIndex >= 0) {
      pdfPages.value.splice(targetGroupIndex, pageCount);
    }

    displayPages.value = getDisplayPages(pdfPages.value);
  } else {
    pdfPages.value = pdfPages.value.filter(
      (page) => page.pdfId !== pdfId || page.pageNumber !== pageNumber
    );
    displayPages.value = displayPages.value.filter(
      (i) => i.pdfId !== pdfId || i.pageNumber !== pageNumber
    );
  }
  cleanupUnusedFile(pdfId);
};

const addNewFileAfterPage = (page) => {
  pdfPages.value = getResult();
  displayPages.value = getDisplayPages(pdfPages.value);
  nextTick(() => {
    const fileInput = document.createElement("input");
    fileInput.type = "file";
    fileInput.accept = "application/pdf,image/jpeg,image/png,image/webp,image/svg+xml,image/bmp,.heic,.heif,.jfif,.pjpeg,.pjp";
    fileInput.multiple = true;
    fileInput.onchange = async (event) => {
      dataLayer.push({
        event: 'file_selected2',
        insert_type: 'file'
      });
      const files = event.target.files;
      // for (let i = 0; i < files.length; i++) {
      //   const file = files[i];
      //   if (file.type !== 'application/pdf') {
      //     window.errorPopup.alert("unsupported");
      //     return;
      //   }
      // }
      const validFiles = await checkFiles(files)

      if (validFiles.length) {
        const insertPages = [];
        const fileIds = [];
        Array.from(validFiles).forEach((file) => {
          bgColor.value += 1;
          const colorIndex = bgColor.value
          const fileId = file.fileId
          const pages = createPagesByFile(file, colorIndex);
          insertPages.push(...pages);
          fileIds.push(fileId);
        });

        const targetIndex = pdfPages.value.findIndex(
          (p) => p.pdfId === page.pdfId && p.pageNumber === page.pageNumber
        );
        const pageCount =
          displayPages.value.find(
            (p) => p.pdfId === page.pdfId && p.pageNumber === page.pageNumber
          ).pageCount || 1;

        if (targetIndex >= 0) {
          pdfPages.value.splice(targetIndex + pageCount, 0, ...insertPages);
        }
        const targetIndex2 = displayPages.value.findIndex(
          (p) => p.pdfId === page.pdfId && p.pageNumber === page.pageNumber
        );
        if (targetIndex2 >= 0) {
          displayPages.value.splice(
            targetIndex2 + 1,
            0,
            ...getDisplayPages(insertPages)
          );
        }

        const currentVersion = renderVersion;
        nextTick(async () => {
          if (currentVersion !== renderVersion) return;
          for (const fileId of fileIds) {
            await renderPages(fileId, currentVersion);
            if (currentVersion !== renderVersion) return;
          }
          initializeSortable();
        });
      }

    };
    fileInput.click();
    closeInsertMenu()
  });
};

const addBlankPage = async (event, page = null) => {
  if(event.currentTarget.classList.contains('pdf-plus-blank')){
    dataLayer.push({
      event: 'upload_file2'
    });
  }
  closeInsertMenu();
  pdfPages.value = getResult();
  displayPages.value = getDisplayPages(pdfPages.value);

  const referencePage = page || pdfPages.value[pdfPages.value.length - 1] || null;
  const blankPage = createBlankPage(await getPageSize(referencePage));
  const blankDisplayPages = getDisplayPages([blankPage]);

  if (page) {
    const targetIndex = pdfPages.value.findIndex(
      (p) => p.pdfId === page.pdfId && p.pageNumber === page.pageNumber
    );
    const pageCount =
      displayPages.value.find(
        (p) => p.pdfId === page.pdfId && p.pageNumber === page.pageNumber
      )?.pageCount || 1;

    if (targetIndex >= 0) {
      pdfPages.value.splice(targetIndex + pageCount, 0, blankPage);
    } else {
      pdfPages.value.push(blankPage);
    }

    const targetIndex2 = displayPages.value.findIndex(
      (p) => p.pdfId === page.pdfId && p.pageNumber === page.pageNumber
    );
    if (targetIndex2 >= 0) {
      displayPages.value.splice(targetIndex2 + 1, 0, ...blankDisplayPages);
    } else {
      displayPages.value.push(...blankDisplayPages);
    }
  } else {
    pdfPages.value.push(blankPage);
    displayPages.value.push(...blankDisplayPages);
  }
  const isHead = page === null && !event.currentTarget.classList.contains('pdf-plus-blank') ? '_head' : ''
  dataLayer.push({
    event: 'file_selected2' + isHead,
    insert_type: 'blank'
  });

  const currentVersion = renderVersion;
  nextTick(async () => {
    if (currentVersion !== renderVersion) return;
    await renderPages(blankPage.pdfId, currentVersion);
    if (currentVersion !== renderVersion) return;
    initializeSortable();
  });
};

const onDragOver = (event) => {
  event.preventDefault();
};

const onDragLeave = (event) => {
  event.preventDefault();
};

const onDrop = async (event) => {
  event.preventDefault();
  const files = event.dataTransfer.files;

  const validFiles = await checkFiles(files)

  if (validFiles.length) {
    addFiles(validFiles);
  }
};

const previewPage = async (event) => {
  const pageInfoElement = event.target.closest('.pdf-page-info');

  if (!pageInfoElement) {
    console.error("No page info element found!");
    return;
  }

  const pdfId = pageInfoElement.dataset.pdfid;
  const pageNumber = Number(pageInfoElement.dataset.pagenumber);

  const pageData = pdfPages.value.find(
    (page) => page.pdfId === pdfId && page.pageNumber === pageNumber
  );

  if (!pageData) {
    console.error("Page data not found!");
    return;
  }

  const appContainer = document.getElementById("operation-box");
  const previewBox = document.createElement("div");
  previewBox.className = "preview-box";

  const previewBoxCont = document.createElement("div");
  previewBoxCont.className = "preview-box-cont";

  const figure = document.createElement("figure");
  previewBoxCont.appendChild(figure);
  previewBox.appendChild(previewBoxCont);
  appContainer.appendChild(previewBox);

  previewBox.addEventListener('click', () => {
    previewBox.remove();
  });

  if (pageData.type === "blank") {
    const canvas = createBlankCanvas(fileMap.value[pdfId], 1200);
    figure.innerHTML = "";
    figure.appendChild(canvas);
    canvas.addEventListener('click', (e) => {
      e.stopPropagation();
    });
    return;
  }

  if (pageData.type === "image") {
    const fileInfo = await ensureImageReady(pdfId);
    const img = new Image();
    img.src = fileInfo?.failed || !fileInfo?.url ? "#" : fileInfo.url;
    img.alt = pageData.pdfName;
    figure.innerHTML = "";
    figure.appendChild(img);
    img.addEventListener('click', (e) => {
      e.stopPropagation();
    });
    return;
  }

  const page = await fileMap.value[pdfId].getPage(pageNumber);

  const viewport = page.getViewport({ scale: 1 });
  const canvas = document.createElement("canvas");
  const context = canvas.getContext("2d", { willReadFrequently: true });

  canvas.width = viewport.width;
  canvas.height = viewport.height;

  try {
    await page.render({
      canvasContext: context,
      viewport: viewport,
    }).promise;
    page.cleanup();
  } catch (error) {
    console.error(
      `Error rendering PDF ${pdfId} page ${pageNumber}:`,
      error
    );
    context.fillStyle = "#fff";
    context.fillRect(0, 0, canvas.width, canvas.height);
  }

  figure.innerHTML = "";
  figure.appendChild(canvas);

  canvas.addEventListener('click', (e) => {
    e.stopPropagation();
  });
};


const initializeSortable = () => {
  const pdfPagesContainer = document.querySelector(".pdf-pages");
  if (!pdfPagesContainer) {
    if (showApp.value) {
      console.error("PDF pages container not found!");
    }
    return;
  }
  if (sortable.value) {
    sortable.value.destroy();
  }
  sortable.value = new Sortable(pdfPagesContainer, {
      animation: 0,
      forceFallback: true,
      fallbackOnBody: true,
      handle: ".page-page-grab",
      filter: ".delete-page-btn",
      preventOnFilter: true,
      onStart: function (evt) {
        const originalImg = evt.item.querySelector(".page-placeholder img");
        if (originalImg) {
          const dragPlaceholder = document.querySelector(
            ".sortable-drag .page-placeholder"
          );
          if (dragPlaceholder) {
            dragPlaceholder.innerHTML = "";
            dragPlaceholder.appendChild(originalImg.cloneNode(true));
          }
          return;
        }

        const originalCanvas = evt.item.querySelector("canvas");
        if (!originalCanvas) {
          console.error("No Canvas element");
          return;
        }

        const originalCtx = originalCanvas.getContext("2d");
        if (!originalCtx) {
          console.error("No Canvas 2D");
          return;
        }

        const imageData = originalCtx.getImageData(
          0,
          0,
          originalCanvas.width,
          originalCanvas.height
        );
        if (imageData.data.every((channel) => channel === 0)) {
          console.warn("Empty Canvas");
        }

        const dataURL = originalCanvas.toDataURL();

        let dragCanvas = document.createElement("canvas");
        dragCanvas.classList.add("page-canvas");

        dragCanvas.width = originalCanvas.width;
        dragCanvas.height = originalCanvas.height;
        const dragCtx = dragCanvas.getContext("2d");

        let dragPlaceholder = document.querySelector(
          ".sortable-drag .page-placeholder"
        );
        if (dragPlaceholder) {
          dragPlaceholder.innerHTML = "";
          dragPlaceholder.appendChild(dragCanvas);
        }

        const img = new Image();
        img.onload = function () {
          dragCtx.drawImage(img, 0, 0);
          if (dragCanvas.toDataURL() === "data:,") {
            console.error("Draw Copied Canvas FAILED !");
          } else {
            console.log("Draw Copied Canvas SUCCESS !");
          }
        };
        img.onerror = function () {
          console.error("failed image");
        };
        img.src = dataURL;
      },
      onMove: (evt) => {
        const { related, dragOverContainer } = evt;

        if (related && related.classList.contains("pdf-page-plus")) {
          return false;
        }

        if (
          dragOverContainer &&
          dragOverContainer.classList.contains("pdf-pages") &&
          related &&
          related.classList.contains("pdf-page-plus")
        ) {
          return false;
        }

        return true;
      },
      onEnd: () => {
        if (isMergedMode.value) {
          const currentVersion = renderVersion;
          setTimeout(async () => {
            if (currentVersion !== renderVersion) return;
            // console.log("##1", pdfPages.value);
            const pdfIdsCurrent = displayPages.value.map((item) => item.pdfId);
            const pdfIdsShuoldBe = getDisplayPages(getResult()).map(
              (item) => item.pdfId
            );
            // console.log("##2", pdfIdsCurrent, pdfIdsShuoldBe);
            const res = pdfIdsCurrent.join(",") !== pdfIdsShuoldBe.join(",");
            if (res) {
              pdfPages.value = getResult();
              displayPages.value = getDisplayPages(getResult());
              // console.log("##3", displayPages.value);
              await renderAllPages(displayPages.value, currentVersion);
              if (currentVersion !== renderVersion) return;
              initializeSortable();
            }
          }, 50);
        }
        $('.sortable-fallback').remove()
        $('.pdf-page-item').removeClass('sortable-chosen sortable-ghost')
      },
    });
};

const triggerFileInput = (event) => {
  if (event.currentTarget.classList.contains('pdf-plus-file')) {
    dataLayer.push({
      event: 'upload_file2'
    });
    fileInput.value.id = 'click-last'
  }else{
    fileInput.value.id = 'click-head'
  }
  
  fileInput.value.click();
};

const getResult = (isExport = false) => {
  const allPages = [...pdfPages.value];
  const pdfPagesContainer = document.querySelector(".pdf-pages");
  if (!pdfPagesContainer) {
    console.error("PDF pages container not found!");
    return [];
  }

  const pageItems = Array.from(pdfPagesContainer.children);
  pageItems.pop();

  const sortedOrder = pageItems.map((item) => {
    const pdfId = item.querySelector(".pdf-page-info")?.id.split("-")[1];
    const pageNumber = item.querySelector(".pdf-page-info")?.id.split("-")[3];
    const pagecount = item.querySelector(".pdf-page-info")?.dataset.pagecount;
    return { pdfId, pageNumber, pagecount };
  });

  // console.log("sortedOrder", sortedOrder);

  let result = [];
  if (isMergedMode.value) {
    for (const item of sortedOrder) {
      const { pdfId, pageNumber, pagecount } = item;
      const index = allPages.findIndex(
        (i) => i.pdfId === pdfId && Number(i.pageNumber) === Number(pageNumber)
      );
      const subArray = allPages.slice(index, index + Number(pagecount));
      result.push(...subArray);
    }
  } else {
    const sortedPdfPages = sortedOrder.map(({ pdfId, pageNumber }) => {
      return allPages.find(
        (page) =>
          page.pdfId === pdfId && Number(page.pageNumber) == Number(pageNumber)
      );
    });

    result = sortedPdfPages;
  }
  // console.log("~~ result :", result);
  if (isExport) {
    dataLayer.push({
      event: 'export_file',
    });
    exportToPdf(result)
  }

  return result;
};

const exportToPdf = async (resultPages) => {
  invalidateRenderTasks();
  dataLayer.push({
    event: 'show_pdfcvert_processing',
  });
  showApp.value = false;
  document.querySelectorAll(".pdfcvert-before, .pdfcvert-upload").forEach(element => {
    element.style.display = "none";
  });
  document.querySelectorAll(".pdfbox, .pdfcvert-converting, .infheader").forEach(element => {
    element.style.display = "block";
  });
  const firstNamedPage = resultPages.find((page) => page.type !== "blank") || resultPages[0];
  const firstFileName = firstNamedPage.pdfName.replace(/\.[^.]+$/, "");
  let fileId;
  try {
    PDFLib = await import('pdf-lib');
    const doc = await PDFLib.PDFDocument.create();
    const sourcePdfCache = new Map();

    for (let pageIndex = 0; pageIndex < resultPages.length; pageIndex++) {
      const page = resultPages[pageIndex];
      const pageIdx = page.pageNumber - 1

      fileId = page.pdfId
      const fileData = fileAll.value[fileId];

      if (page.type === "blank") {
        addBlankImagePage(doc, fileMap.value[fileId]);
        continue;
      }

      if (page.type === "image") {
        await addImageToPdf(doc, page);
        continue;
      }

      if (fileData) {
        let sourcePdfDoc = sourcePdfCache.get(fileId);
        if (!sourcePdfDoc) {
          sourcePdfDoc = await PDFLib.PDFDocument.load(fileData);
          sourcePdfCache.set(fileId, sourcePdfDoc);
        }
        const [embeddedPage] = await doc.copyPages(sourcePdfDoc, [pageIdx]);

        doc.addPage(embeddedPage);
      }
    }

    const pdfBytes = await doc.save();
    const fileUrl = URL.createObjectURL(new Blob([pdfBytes], { type: 'application/pdf' }));
    downloadFileUrl = fileUrl;
    document.querySelector(".pdfcvert-converting").style.display = "none";
    document.querySelector(".pdfcvert-complete").style.display = "block";
    $('.continue-tools').slideDown();
    if (window.innerWidth < 769) {
      document.querySelector(".pdfcvert-banner-text").style.display = "none"
    }
    document.querySelector(".pdfcvert-complete-name").innerText = firstFileName + '_AddPage.pdf';
    window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
    const downloadLink = document.querySelector(".pdfcvert-download");
    if (downloadLink) {
      downloadLink.href = fileUrl;
      downloadLink.download = firstFileName + '_AddPage.pdf';
      downloadLink.style.display = 'block';
      resetPages()
    }
    dataLayer.push({
      event: 'show_pdfcvert_complete',
    });
  } catch (error) {console.log(error);
    if (error.toString().includes("is encrypted")) {
      let errorName = resultPages.find(item => item.pdfId === fileId)?.pdfName;
      if (errorName.length > 8) {
        errorName = errorName.slice(0, 8) + "..."
      }
      window.errorPopup.alert("encrypted", errorName);
    } else {
      window.errorPopup.alert("unknown");
    }
    console.error('Error export PDF:', error);
    OnloadCvert();
    showApp.value = false;
    resetPages();
  }
};
</script>

<style>
body {
  margin: 0;
}

html canvas {
  display: block;
  max-width: 100%;
  max-height: 100%;
  width: auto;
  height: auto;
}
.pdfcvert-complete-box{justify-content: center;}
.pdfcvert-complete-ready h4{display: flex; align-items: center; justify-content: center; gap: 8px;}
.pdfcvert-complete-ready h4::before{content: ""; width: 24px; height: 24px; background: url('https://www.pdfgear.com/img/new-tools/green-tick.svg') center no-repeat; background-size: 24px;}
.page-placeholder .page-img-base{max-width: 100%; height: auto; aspect-ratio: 214 / 297; display: flex;}
.image-landscape .page-placeholder .page-img-base{aspect-ratio: 297 / 214;}
.page-placeholder img{width: auto; height: auto; max-width: 100%; max-height: 100%; box-shadow: #D2D2D2 0px 0px 0px 1px; position: relative; z-index: 1; margin: auto;}
.page-placeholder canvas {
  box-shadow: #D2D2D2 0px 0px 0px 2px;
}

.multiple-pages .page-placeholder canvas {
  box-shadow: #D2D2D2 0px 0px 0px 2px, #F5F5F5 -28px -20px 0px 0px, #D2D2D2 -28px -20px 0px 2px;
}

@media screen and (max-width: 1024px) {
  .page-placeholder canvas {
    box-shadow: #D2D2D2 0px 0px 0px 1px;
  }

  .multiple-pages .page-placeholder canvas {
    box-shadow: #D2D2D2 0px 0px 0px 1px, #F5F5F5 -6px -5px 0px 0px, #D2D2D2 -6px -5px 0px 1px;
  }
}

.preview-box {
  position: fixed;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  z-index: 111;
  background: #00000080;
}

.preview-box-cont {
  position: absolute;
  left: 0;
  top: 0;
  right: 0;
  bottom: 0;
  margin: auto;
  max-width: 980px;
  width: 90%;
  max-height: 80%;
}

.preview-box-cont::after {
  content: "";
  position: absolute;
  top: -50px;
  right: 0;
  width: 44px;
  height: 44px;
  border-radius: 50%;
  background: #fff url("https://www.pdfgear.com/img/new-tools/cross.svg") center no-repeat;
  background-size: 16px;
}

.preview-box-cont figure {
  max-width: 100%;
  max-height: 100%;
  margin: 0;
  width: 100%;
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
}

.preview-box-cont img {
  display: block;
  max-width: 100%;
  max-height: 100%;
}
</style>

<style scoped>
* {
  margin: 0;
  padding: 0;
}

.operation-error {
  position: fixed; z-index: 3;
  margin: 0 auto;
  left: 0;
  right: 0;
  bottom: 20px;
  background: #FF003D;
  border-radius: 4px;
  padding: 8px;
  padding-inline-start: 16px;
  box-sizing: border-box;
  width: 90%;
  max-width: 650px;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.operation-error::before {
  content: "";
  display: block;
  width: 20px;
  height: 20px;
  margin-inline-end: 8px;
  background: url("https://www.pdfgear.com/img/new-tools/tips-red.svg") center no-repeat;
  background-size: contain;
  flex-shrink: 0;
}

.operation-error p {
  font-size: 14px;
  color: #fff;
  flex: 1;
}

.operation-error-close {
  color: #fff;
  width: 20px;
  height: 20px;
  line-height: 18px;
  text-align: center;
  font-weight: 300;
  font-size: 20px;
  cursor: pointer;
  flex-shrink: 0;
}

.operation-box {
  width: 100%;
  height: 100vh;
  overflow: hidden;
  background-color: #F0F0F0;
}

.action-bar {
  border-bottom: 1px solid #eee; box-shadow: 0px 4px 8px 0px #0000001A;
  background: #fff;
}

.action-bar-cont {
  width: 100%;
  max-width: 1200px;
  height: 62px;
  margin: auto;
  display: flex;
  justify-content: space-between;
  align-items: center;
  box-sizing: border-box;
}

.action-bar-cont>div {
  display: flex;
  gap: 24px;
  align-items: center;
}
.add-file-btn{font-size: 14px; color: #000; padding-inline: 4px; border-radius: 8px; border: 1px solid #eee; cursor: pointer; display: flex; align-items: center; position: relative;}
.add-file-btn::before{content: ""; width: 32px; height: 32px; background: url("https://www.pdfgear.com/img/new-tools/add-pages-to-pdf/add-file.svg") center no-repeat; background-size: 32px;}
.add-file-btn::after{content: ""; margin-inline-start: 12px; width: 16px; height: 16px; background: url("https://www.pdfgear.com/img/new-tools/arrow-down.svg") center no-repeat; background-size: 16px; }
.clear-pdf-btn{font-size: 14px; color: #000; padding-inline: 8px; border-radius: 8px; border: 1px solid #eee; cursor: pointer; display: flex; align-items: center;}
.clear-pdf-btn::before{content: ""; width: 32px; height: 32px; background: url("https://www.pdfgear.com/img/new-tools/clear.svg") center no-repeat; background-size: 32px;}

.drag-describe {
  font-size: 14px;
  color: #606060;
  display: flex;
  gap: 8px;
  align-items: center;
}

.drag-describe::before {
  content: "";
  width: 14px;
  height: 14px;
  background: url("https://www.pdfgear.com/img/new-tools/tool-tips.svg") center no-repeat;
  background-size: contain;
}

.toggle-switch{display: flex; align-items: center; gap: 4px; padding: 4px; border-radius: 8px; background: #F0F0F0; user-select: none;}
.toggle-switch>div{display: flex; align-items: center; gap: 2px; padding: 4px 8px; border-radius: 4px; cursor: pointer;}
.toggle-switch p{font-size: 14px; color: #000;}
.toggle-text-short{display: none;}
.toggle-switch figure{width: 24px; height: 24px; background-size: 24px;}
.page-view figure{background: url("https://www.pdfgear.com/img/new-tools/add-pages-to-pdf/page-view.svg");}
.file-view figure{background: url("https://www.pdfgear.com/img/new-tools/add-pages-to-pdf/file-view.svg");}
.page-view.act figure{background: url("https://www.pdfgear.com/img/new-tools/add-pages-to-pdf/page-view-act.svg");}
.file-view.act figure{background: url("https://www.pdfgear.com/img/new-tools/add-pages-to-pdf/file-view-act.svg");}
.toggle-switch .act{background: #fff;}
.toggle-switch .act p{color: #0088FF;}

.export-pdf-btn {
  width: 222px;
  line-height: 40px;
  font-size: 16px;
  font-weight: 700;
  color: #fff;
  background: #0088FF;
  border-radius: 8px;
  border: none;
  cursor: pointer;
}

.export-pdf-btn:disabled {
  background: #aaa;
  color: #FFFFFF80;
  cursor: not-allowed;
}

.pdf-views {
  text-align: center;
  overflow-y: auto;
  overscroll-behavior: none;
  height: calc(100vh - 62px);
}

.pdf-pages {
  display: flex;
  flex-wrap: wrap;
  gap: 24px;
  margin: 0 auto;
  padding: 20px;
  width: 100%;
  max-width: 1680px;
  box-sizing: border-box;
}

.pdf-page-item {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 24px;
}

.pdf-page-info {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 12px;
  position: relative;
  box-sizing: border-box;
  position: relative;
}

.page-page-grab {
  position: absolute;
  width: 100%;
  height: 100%;
  inset-inline-start: 0;
  top: 0;
  z-index: 2;
}

.multiple-pages .page-placeholder {
  padding: 28px 0 0 20px;
}
.image-type .page-placeholder{position: relative; padding: 4%;}
.image-type .page-placeholder::before{content: ""; position: absolute; width: 100%; height: 100%; background: #fff;}
.image-landscape .page-placeholder::before{height: auto; aspect-ratio: 297 / 214;}


.page-placeholder {
  /* background-color: #ddd; border: 1px solid #d2d2d2; */
  width: 214px;
  height: 297px;
  box-sizing: border-box;
  display: flex;
  justify-content: center;
  align-items: center;
}
.page-loading{
  width: 100%; height: 100%; box-sizing: border-box; border: 2px solid #d2d2d2; overflow: hidden; position: relative; background: #fff; 
}
.page-loading::after{ content: ""; width: 100%; height: 100%; background: url("https://www.pdfgear.com/img/new-tools/merge-pdf/loading.svg") center no-repeat; background-size: 40px; display: block; animation: rotate 2s infinite linear;}
@keyframes rotate {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

.page-text {
  max-width: 214px;
}

.page-text p {
  font-size: 14px;
  color: #606060;
  line-height: 1.5;
  text-align: center;
}

.page-file-name {
  margin: 12px auto 2px;
  padding: 0 4px;
  border-radius: 4px;
  background-color: #fff;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  max-width: 100%;
  box-sizing: border-box;
}

.name-bgcolor-1 {
  background: #F2F3C0;
}

.name-bgcolor-2 {
  background: #F3C1C0;
}

.name-bgcolor-3 {
  background: #F3D8C0;
}

.name-bgcolor-4 {
  background: #C0F3E2;
}

.name-bgcolor-5 {
  background: #D8C0F3;
}

.name-bgcolor-6 {
  background: #BCF0FF;
}

.name-bgcolor-7 {
  background: #D4E0D4
}

.name-bgcolor-0 {
  background: #CEF3C0;
}
.name-bgcolor-blank {
  background: #fff;
}

.pdf-page-insert{width: 44px; height: 44px; position: relative;}
.act .pdf-insert-close, .act .pdf-insert-menu{display: block;}
.pdf-insert-close{display: none; position: fixed; z-index: 3; inset: 0px; background: rgba(0, 0, 0, 0); cursor: default;}
.pdf-insert-menu{display: none; padding: 4px; border-radius: 8px; background: #F7F7F8; box-shadow: 0px 10px 20px 0px #0000001A; width: fit-content; position: absolute; z-index: 3; top: 46px; left: 50%; transform: translateX(-50%); box-sizing: border-box;}
.pdf-insert-menu li{font-size: 14px; color: #000; min-width: 120px; height: fit-content; line-height: 24px; padding: 6px; border-radius: 6px; box-sizing: border-box; user-select: none; text-align: start; cursor: pointer; white-space: nowrap;}
.pdf-insert-menu li:hover{background: #fff; color: #0088FF; font-weight: 700;}
.add-file-btn .pdf-insert-menu{top: 40px; inset-inline-start: 0; transform: none;}

.pdf-page-add {
  background: #0088FF33 url("https://www.pdfgear.com/img/new-tools/add-white.svg") center no-repeat;
  background-size: 20px;
  height: 44px;
  width: 44px;
  border-radius: 50%;
  cursor: pointer;
  border: none; flex-shrink: 0;
}

.pdf-page-add:hover {
  background-color: #0088FF;
}

.pdf-canvas {
  width: 100%;
  height: 100%;
}

.add-pdf-btn {
  padding: 10px 20px;
  line-height: 40px;
  background-color: #4caf50;
  color: white;
  border: none;
  cursor: pointer;
}

.delete-page-btn {
  background: #555 url("https://www.pdfgear.com/img/new-tools/trash-white.svg") center no-repeat;
  background-size: 16px 18px;
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: none;
  cursor: pointer;
  position: absolute;
  top: 24px;
  inset-inline: 0;
  margin: 0 auto;
  opacity: 0;
  z-index: 2;
}

.pdf-page-info:hover .delete-page-btn {
  opacity: 1;
  transition: .2s;
}

.sortable-ghost .pdf-page-info::after {
  content: "";
  position: absolute;
  left: 0;
  top: 0;
  width: 100%;
  height: 100%;
  background: #0088ff1a;
  border: 1px solid #0088ffcc;
  border-radius: 6px;
  box-sizing: border-box;
  z-index: 1;
}

.sortable-ghost .pdf-page-info .delete-page-btn,
.sortable-ghost .pdf-page-info .page-page-grab,
.sortable-ghost .pdf-page-info figure,
.sortable-ghost .pdf-page-info p {
  opacity: 0;
}

.sortable-drag {
  box-shadow: 0px 30px 40px 0px #00000033;
  width: auto !important;
  height: auto !important;
  background: #fafafa;
}

.sortable-drag .pdf-page-insert {
  display: none;
}

.sortable-drag .delete-page-btn {
  display: none;
}

.pdf-page-item:nth-last-of-type(2) .pdf-page-insert {
  opacity: 0; visibility: hidden; pointer-events: none;
}

.pdf-page-plus {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 6px;
  width: 214px;
  height: 352px;
  margin: 12px;
  border-radius: 6px;
  border: 1px dashed #0088FFCC;
  background: #0088FF1A;
  cursor: pointer;
  box-sizing: border-box;
  position: relative;
}
.pdf-page-plus>p {display: none;
  font-size: 14px;
  color: #606060;
}
.pdf-plus-file{font-size: 20px; font-weight: 700; color: #0088FF; padding-bottom: 48px; margin-bottom: 8px; border-bottom: 1px dashed #0088FF33;}
.pdf-plus-file::before {
  content: ""; display: block; margin: auto auto 16px;
  width: 44px;
  height: 44px;
  background: #08f url("https://www.pdfgear.com/img/new-tools/add-white.svg") center no-repeat;
  background-size: 20px;
  border-radius: 50%;
}
.pdf-plus-file::after{content: ""; position: absolute; inset-inline: 0; inset-block: 0; width: 100%; height: 100%; z-index: 0;}
.pdf-plus-blank{font-size: 18px; color: #0088FF; line-height: 36px; padding-inline: 14px; display: flex; align-items: center; border-radius: 18px; position: relative; z-index: 1; text-decoration: underline;}
.pdf-plus-blank:hover{background: #fff;}
.pdf-plus-blank::before{content: ""; width: 36px; height: 36px; background: url("https://www.pdfgear.com/img/new-tools/add-pages-to-pdf/add-blank-page.svg") center no-repeat; background-size: contain;}


@media screen and (max-width: 1690px) and (min-width: 1025px) {
  .pdf-views .pdf-page-item{width: calc((100% - 4 * 24px) / 5);}
  .pdf-views .pdf-page-info{width: calc(100% - 44px - 24px); max-width: 238px; aspect-ratio: 238 / 377;}
  .pdf-views .page-placeholder{width: 100%; height: auto; max-width: 214px; aspect-ratio: 214 / 297;}
  .pdf-views .page-text{max-width: 100%;}
  .pdf-views .pdf-page-add{flex: 0 0 44px;}
  .pdf-page-plus{width: calc((100% - 4 * 24px) / 5 - 44px - 24px); height: auto; aspect-ratio: 214 / 352;}
}
@media screen and (max-width: 1280px) and (min-width: 1025px) {
  .pdf-views .pdf-page-item{width: calc((100% - 3 * 24px) / 4);}
  .pdf-page-plus{width: calc((100% - 3 * 24px) / 4 - 44px - 24px);}
}
@media screen and (min-width: 1024px) {
  .pdf-page-info:hover {
    background: #0088FF33;
    box-shadow: 0 0 0 1px inset #0088FFCC;
  }

  .pdf-page-plus:hover {
    background: #0088FF40;
  }
}

@media screen and (max-width: 1280px) {
  .action-bar-cont {
    padding: 0 20px;
  }
}

@media screen and (max-width: 1024px) {
  .action-bar-cont>div{gap: 18px;}
  .toggle-switch p, .drag-describe{display: none;}
  .toggle-switch .act .toggle-text-short{display: block;}
  .add-file-btn{border-color: #333; padding-inline-end: 0;}
  .add-file-btn::after{border-inline-start: 1px solid #eee; padding: 5px;}
  .pdf-pages {
    flex-direction: column;
    gap: 8px;
  }

  .pdf-page-item {
    flex-direction: column;
    gap: 8px;
    touch-action: manipulation;
    user-select: none;
  }

  .pdf-page-info {
    flex-direction: row;
    gap: 24px;
    background: #FAFAFA;
    border-radius: 8px;
    padding: 8px 4px;
    width: 100%;
  }

  .page-page-grab {
    content: "";
    width: 23px;
    height: 60px;
    background: url("https://www.pdfgear.com/img/new-tools/dot6-blue.svg") center no-repeat;
    background-size: 11px 44px;
    cursor: grab;
    top: calc(50% - 30px);
    inset-inline-start: -3px;
  }
  .image-type .page-placeholder{padding: 4px;}

  .page-placeholder {
    width: 107px;
    height: 149px;
    margin-inline-start: 33px;
  }

  .multiple-pages .page-placeholder {
    padding: 6px 0 0 5px;
  }

  .page-text p {
    text-align: start;
    font-size: 16px;
  }

  .page-file-name {
    margin: 0 0 8px;
    padding: 0 7px;
  }

  .delete-page-btn {
    width: 44px;
    height: 44px;
    background: url("https://www.pdfgear.com/img/new-tools/trash-black.svg") center no-repeat;
    top: 50%;
    left: auto;
    inset-inline-end: 4px;
    margin: 0;
    transform: translateY(-50%);
    opacity: 1;
  }

  .pdf-page-insert{width: 100%;}
  .act .pdf-insert-closemo{content: ""; position: absolute; width: 24px; height: 24px; border-radius: 50%; background: #fff url(https://www.pdfgear.com/img/new-tools/cross.svg) center no-repeat; background-size: 12px; inset: auto; inset-block-start: 50%; inset-inline-end: 4px; transform: translateY(-50%); z-index: 3;}
  .pdf-page-insert.act .pdf-insert-menu{display: flex;}
  .pdf-page-insert.act .pdf-insert-menu{width: 100%; top: 0; justify-content: center; gap: 12px;}
  .pdf-page-insert.act .pdf-insert-menu li{background: #fff; font-weight: 500 !important; color: #0088FF; display: flex; justify-content: center; align-items: center; gap: 4px;}
  .pdf-page-insert.act .pdf-insert-menu li::before, .pdf-plus-menu li::before{content: ""; width: 16px; height: 16px; background: url("https://www.pdfgear.com/img/new-tools/add-blue.svg") center no-repeat; background-size: 12px; margin: 0;}
  .pdf-page-plus>p{display: block;}
  .pdf-plus-file::after{display: none;}
  .pdf-plus-menu{display: flex; width: 100%; justify-content: center; gap: 12px;}
  .pdf-plus-menu li{padding: 6px; border-radius: 6px; font-size: 14px; background: #fff; font-weight: 500 !important; display: flex; justify-content: center; align-items: center; gap: 4px; margin: 0; line-height: 24px; border: none; min-width: 120px; text-decoration: none;}
  

  .pdf-page-add {
    background: #fff url("https://www.pdfgear.com/img/new-tools/add-blue.svg") center no-repeat;
    background-size: 12px;
    width: 24px;
    height: 24px; margin: 10px;
  }

  .pdf-page-add:hover {
    background-color: #fff;
  }

  .pdf-page-plus {
    margin: 0;
    width: 100%;
    height: 165px;
  }

  .sortable-drag {
    width: calc(100% - 40px) !important;
  }
}

@media screen and (max-width: 750px) {
  .operation-error {
    bottom: 95px;
  }

  .operation-box {
    height: calc(100vh - 74px);
  }

  .action-bar-cont {
    padding: 0 6px;
  }

  .action-bar-right {
    position: fixed;
    z-index: 3;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100%;
    align-items: center;
    justify-content: center;
    height: 74px;
    background: #fff;
    border-top: 1px solid #aaa;
    padding: 0 20px;
    box-sizing: border-box;
  }
  .action-bar-cont>div{justify-content: space-between;}

  .toggle-mode {
    font-size: 16px;
  }

  .export-pdf-btn {
    width: 168px;
    line-height: 50px;
    font-size: 20px;
  }

  .pdf-views {
    height: calc(100vh - 136px);
  }
  .pdf-pages{padding-bottom: 120px;}
}

@media screen and (max-width: 640px) {
  .pdf-page-info {
    gap: 6px;
  }

  .page-placeholder {
    width: 46px;
    height: 64px;
    margin-inline-start: 15px;
  }
.page-loading::after{background-size: 24px;}
  .pdf-page-plus {
    height: 80px;
  }
}
</style>
